import React, { Fragment, useEffect, useContext, useState } from "react";
import { Switch, Route } from "react-router-dom";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

// States
// import LayoutContext from "./context/layout/LayoutContext";
import AlertContext from "./context/alert/AlertContext";
import AuthContext from "./context/auth/AuthContext";

// Components
// Layout
import PrivateRoute from "./components/routing/PrivateRoute";
import Navbar from "./components/layout/Navbar-v3";
import Sidebar from "./components/layout/Sidebar-v3";
import Alerts from "./components/layout/Alerts-v1";
import Alerts_v2 from "./components/layout/Alerts-v2";
import Backdrops from "./components/layout/Backdrop";

// Guest Pages
import PageNotFound from "./components/layout/PageNotFound";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register"
import ForgetPassword from "./components/auth/ForgetPassword";
import ResetPassword from "./components/auth/ResetPassword";
import ResetSucceeded from "./components/auth/ResetSucceeded";

// Dashboard & Change Password
import ChangePassword from "./components/auth/ChangePassword";
import Dashboard from "./components/dashboard/Dashboard";

// Shopify - Order
import OrderHome from "./components/orders/v1/search/OrderHome";
import OrderEdit from "./components/orders/v1/edit/OrderEdit";

// Shopify - Campaign
import CampaignSearch from "./components/campaigns/search/CampaignSearchHome";
import CampaignContent from "./components/campaigns/content/CampaignForm";

// Avatar
import Avatar from "./components/avatar/avatar";

// Shopify - Report
import Report from "./components/shopify/reports/Report";

// Shopify - Fulfillment
import FulfillmentHome from "./components/fulfillment/FulfillmentHome";

// Shopify - Tracking
import PaypalTracking from "./components/campaigns/paypal-tracking";

// Shopify - Monitoring
import MonitoringHome from "./components/monitoring/MonitoringHome";

// Shopify - Discount
import Discount from "./components/shopify/discounts/Discount";

// Shopify - Domain
import Domain from "./components/shopify/domain/Domain";

// Shopify - Scaling
import ScalingManagement from "./components/shopify/scaling/Scaling";

// Facebook - Profiles
import FacebookProfile from "./components/facebook_profiles/FacebookProfile";

// Shopify - Facebook Error
import FacebookErrors from "./components/facebook_errors/FacebookErrors";

// Shopify - Facebook Status Log
import FacebookStatusLog from "./components/facebook_status_log/StatusLog";

// Paypal - Dispute
import PaypalDispute from "./components/paypal/disputes/DisputeHome";

// Paypal - Payment
import PaypalPayment from "./components/paypal/payments/PaymentHome";

// Setting - Stores
import Store from "./components/stores/v1/Store";

// Setting - Product
import ProductHome from "./components/products/ProductHome";

// Tech - Logs
import LogsHome from "./components/logs/LogsHome";

// Tech - Users
import UserHome from "./components/users/UserHome";
import UserCreation from "./components/users/UserCreation";

import Checkout from "./components/checkout/Checkout"
import ResetFailed from "./components/auth/ResetFailed";

const SwitchRoutes = (props) => {
  const { isAuthenticated, user, token } = useContext(AuthContext);
  const { clearAlert } = useContext(AlertContext);

  const drawerWidth = 240;
  const containerHeight = window.innerHeight;
  // const [open, setOpen] = useState(true);
  const [open, setOpen] = useState(token ? true : false);

  // Aureas Media CRM Theme
  const theme = createTheme({
    palette: {
      primary: {
        light: "#6fc4de",
        main: "#6f9fde",
        dark: "#1a73e8",
        contrastText: "#ffffff",
      },
      secondary: {
        light: "#27b8cf",
        main: "#1864de",
        dark: "#0d3f8f",
        contrastText: "#000000",
      },
    },
    typography: {
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
      fontSize: 12,
    },
    overrides: {
      MuiTableRow: {
        root: {
          "&$hover:hover, &$selected:hover": {
            backgroundColor: "#f1f0f4",
          },
          "&$selected": {
            backgroundColor: "#e3e2e9",
          },
        },
      },
      MuiButton: {
        root: {
          textTransform: "none",
          "&:focus": {
            outline: "none",
          },
        },
      },
      MuiButtonBase: {
        root: {
          textTransform: "none",
          "&:focus": {
            outline: "none",
          },
        },
      },
      MuiIconButton: {
        root: {
          textTransform: "none",
          "&:focus": {
            outline: "none",
          },
        },
      },
      MuiLink: {
        root: {
          cursor: "pointer",
        },
      },
    },
  });

  const useStyles = makeStyles((theme) => ({
    content: {
      // marginTop: "4rem",
      paddingTop: 85,
      padding: theme.spacing(3),
      minHeight: containerHeight,
      backgroundColor: "#eaeaea",
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    contentShift: {
      // marginTop: "4rem",
      // padding: theme.spacing(3),
      // minHeight: containerHeight,
      // backgroundColor: "#eaeaea",
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    },
  }));

  const classes = useStyles();

  const viewWidth = () => {
    if (window.innerWidth < 1200) setOpen(false);
    else setOpen(true);
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      viewWidth(); // On Load Sidebar

      // Add Listener To Detect View Width Size
      window.addEventListener("resize", viewWidth);
    }

    // Remove Listener To Detect View Width Size
    return () => window.removeEventListener("resize", viewWidth);
  }, [isAuthenticated, user]);

  // Clear alert when route changed
  useEffect(() => {
    props.history.listen((location, action) => {
      clearAlert();
    });
  }, [props.history, clearAlert]);

  return (
    <Fragment>
      <ThemeProvider theme={theme}>
        <Navbar openState={[open, setOpen]} drawerWidth={drawerWidth} />
        <Sidebar openState={[open, setOpen]} drawerWidth={drawerWidth} />
        <div
          className={clsx(classes.content, {
            [classes.contentShift]: open,
          })}
        >
          <Alerts />
          <Alerts_v2 />
          <Backdrops />
          <Switch>
            {/* Dashboard */}
            <PrivateRoute exact path="/" component={Dashboard} />
            {/* Shopify - Order */}
            <PrivateRoute exact path="/order" component={OrderHome} />
            <PrivateRoute exact path="/order/edit" component={OrderEdit} />
            {/* Shopify - Campaign */}
            <PrivateRoute
              exact
              path="/campaign"
              component={CampaignSearch}
            />
            <PrivateRoute
              path="/campaign/active"
              component={CampaignContent}
            />
            <PrivateRoute
              path="/avatar"
              component={Avatar}
            />
            {/* Shopify - Report */}
            <PrivateRoute excat path="/report" component={Report} />
            {/* Shopify - Fulfillment */}
            <PrivateRoute
              exact
              path="/fulfillment"
              component={FulfillmentHome}
            />
            {/* Shopify - Tracking */}
            <PrivateRoute exact path="/tracking" component={PaypalTracking} />
            {/* Shopify - Monitoring */}
            <PrivateRoute exact path="/monitoring" component={MonitoringHome} />
            {/* Shopify - Discount */}
            <PrivateRoute path="/discount" component={Discount} />
            {/* Shopify - Domain */}
            <PrivateRoute path="/domain" component={Domain} />
            {/* Shopify - Domain */}
            <PrivateRoute path="/scaling" component={ScalingManagement} />
            {/* Facebook - Profile */}
            <PrivateRoute
              path="/facebook/profile"
              component={FacebookProfile}
            />
            {/* Facebook - Error */}
            <PrivateRoute
              exact
              path="/facebook/error"
              component={FacebookErrors}
            />
            {/* Facebook - Status Log */}
            <PrivateRoute
              exact
              path="/facebook/status"
              component={FacebookStatusLog}
            />
            {/* Paypal - Dispute */}
            <PrivateRoute
              exact
              path="/paypal/dispute"
              component={PaypalDispute}
            />
            {/* Paypal - Payment */}
            <PrivateRoute
              exact
              path="/paypal/payment"
              component={PaypalPayment}
            />
            {/* Setting - Stores */}
            <PrivateRoute path="/store" component={Store} />
            {/* Setting - Products */}
            <PrivateRoute exact path="/product" component={ProductHome} />
            {/* Tech - Logs */}
            <PrivateRoute exact path="/logs" component={LogsHome} />
            {/* Tech - Users */}
            <PrivateRoute exact path="/user" component={UserHome} />
            <PrivateRoute exact path="/user/create" component={UserCreation} />
            {/* Change Password */}
            <PrivateRoute
              exact
              path="/change-password"
              component={ChangePassword}
            />
            
            <Route
              exact 
              path="/checkout"
              component={Checkout}
            />
            {/* Guest Pages */} <Route exact path="/login" component={Login} />
            <Route exact path="/register" component={Register} />
            <Route exact path="/forget-password" component={ForgetPassword} />
            <Route exact path="/reset-password" component={ResetPassword} />
            <Route exact path="/reset-succeeded" component={ResetSucceeded} />
            <Route exact path="/reset-failed" component={ResetFailed} />
            <Route component={PageNotFound} />
          </Switch>
        </div>
        {/* </div> */}
      </ThemeProvider>
    </Fragment>
  );
};

export default SwitchRoutes;
